import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Modal, message as antMessage } from 'antd';
import { SettingOutlined, FileTextOutlined, ToolOutlined, HistoryOutlined, ControlOutlined } from '@ant-design/icons';
import ChatInterface from '../components/ChatInterface';
import ConversationHistoryPanel from '../components/ConversationHistoryPanel';
import AgentConfigDialog from '../components/AgentConfigDialog';
import { streamDataChat, getAgentInfo, getAgentInfoWithContext, getDataConversations, getDataConversationMessages } from '../services/dataService';
import { useModel } from '../contexts/ModelContext';
import { useAuth } from '../contexts/AuthContext';
import { logInteraction } from '../services/adminService';
import { useDashboard } from '../contexts/DashboardContext';

function DataChat({ registerChatReset }) {
  const chatRef = useRef(null);
  const { selectedModel, refreshModels } = useModel();
  const { userEmail, userProfile } = useAuth();
  const sessionIdRef = useRef('data-' + Date.now());
  const { addItem, isOpen, toggleDashboard, focusedComponent, clearFocusedComponent, buildDashboardContext, updateItem, dashboards, activeDashboardId, createDashboard, createAndOpenDashboard, switchDashboard, openDashboard, globalDashboards, chartEditorOpen, getChartEditorState, applyChartEditorEdit, addCustomFilter, patchCustomFilter, updateDashboardMetadata, filterEditorState } = useDashboard();
  const navigate = useNavigate();
  const [systemPromptModal, setSystemPromptModal] = useState({ visible: false, prompt: '', loading: false });
  const [toolsModal, setToolsModal] = useState({ visible: false, tools: [], loading: false });
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  // Conversation history panel state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  const loadConversations = useCallback(async (search = '') => {
    setConversationsLoading(true);
    try {
      const params = { user: userEmail, limit: 50 };
      if (search) params.search = search;
      const data = await getDataConversations(params);
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setConversationsLoading(false);
    }
  }, [userEmail]);

  // Load conversations when panel first opens
  useEffect(() => {
    if (historyPanelOpen) loadConversations();
  }, [historyPanelOpen, loadConversations]);

  const handleSelectConversation = useCallback(async (selectedSessionId) => {
    try {
      const data = await getDataConversationMessages(selectedSessionId);
      if (data.messages && chatRef.current) {
        // Build message objects compatible with ChatInterface state
        const chatMessages = [];
        for (const msg of data.messages) {
          if (msg.role === 'user') {
            chatMessages.push({ role: 'user', content: msg.content });
          } else {
            chatMessages.push({
              role: 'assistant',
              content: msg.content,
              qaRecordId: msg.id,
              tools: [], // Historical messages don't have tool trace in this format
            });
          }
        }
        // Restore conversation via the imperative handle
        chatRef.current.loadConversation(chatMessages);
        sessionIdRef.current = selectedSessionId;
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  }, []);

  const handleConversationSearch = useCallback((keyword) => {
    loadConversations(keyword);
  }, [loadConversations]);

  useEffect(() => {
    if (registerChatReset && chatRef.current) {
      registerChatReset('/data/chat', () => {
        chatRef.current.reset();
        sessionIdRef.current = 'data-' + Date.now();
        // Refresh conversations list so the old conversation appears
        if (historyPanelOpen) loadConversations();
      });
    }
  }, [registerChatReset, historyPanelOpen, loadConversations]);

  const handleSendMessage = async (question, conversationHistory, onMessage, onError, onComplete) => {
    // The Data Agent has exactly three modes — exactly one is active at a time:
    //   • default            — neither dashboard nor chart-editor open
    //   • dashboard-builder  — dashboard panel open, chart editor not open
    //   • pro-chart-editor   — chart editor open (takes precedence; the editor
    //                          opens INSIDE the dashboard, so a dashboard is
    //                          always also open at this point, but the chat
    //                          should focus on the editor only)
    //
    // We send only the context for the active mode so the backend never has to
    // disambiguate. The agent enforces the same precedence defensively.
    let dashboardContext = null;
    let chartEditorContext = null;
    let resolvedMode = 'default';
    if (chartEditorOpen) {
      const state = getChartEditorState() || {};
      chartEditorContext = {
        sql: state.sql || '',
        columns: state.columns || [],
        sample_rows: (state.rows || []).slice(0, 20),
        figure: state.figure || null,
      };
      resolvedMode = 'pro-chart-editor';
    } else if (isOpen) {
      dashboardContext = buildDashboardContext();
      resolvedMode = dashboardContext ? 'dashboard-builder' : 'default';
    }
    // Always attach the user's accessible global dashboards so the
    // agent can call list_global_dashboards / open_dashboard even when
    // the dashboard panel isn't currently open. Build a minimal record
    // — just identity + name/description — so the context payload
    // stays compact even with many dashboards.
    const accessibleGlobals = (globalDashboards || []).map((d) => ({
      uuid: d.uuid,
      name: d.name,
      description: d.description || null,
    }));
    if (accessibleGlobals.length > 0) {
      if (!dashboardContext) dashboardContext = { dashboard_open: false };
      dashboardContext.accessible_global_dashboards = accessibleGlobals;
    }
    // Diagnostic log so the agent's mode is visible in DevTools. Filter
    // by "[data-agent]" in the browser console to confirm chart-editor
    // mode payloads when troubleshooting "agent isn't seeing the editor".
    try {
      console.log('[data-agent] sending request', {
        mode: resolvedMode,
        has_dashboard_context: !!dashboardContext,
        has_chart_editor_context: !!chartEditorContext,
        chart_editor_summary: chartEditorContext && {
          sql_len: (chartEditorContext.sql || '').length,
          columns: chartEditorContext.columns,
          sample_row_count: (chartEditorContext.sample_rows || []).length,
          figure_trace_count: ((chartEditorContext.figure || {}).data || []).length,
        },
      });
    } catch { /* console may be unavailable in some contexts */ }
    await streamDataChat(question, conversationHistory, onMessage, onError, onComplete, selectedModel, userEmail, userProfile, sessionIdRef.current, dashboardContext, chartEditorContext);
  };

  const handleChartEditorEdit = async ({ sql, figure, reason }) => {
    const editResult = await applyChartEditorEdit({ sql, figure });
    const parts = [];
    if (editResult?.appliedSql) {
      if (editResult.runError) {
        parts.push(`SQL updated, but the query failed: ${editResult.runError}`);
      } else if (editResult.runResult) {
        const { columns: cols = [], rowCount = 0 } = editResult.runResult;
        parts.push(`SQL updated and re-run — ${rowCount} rows × ${cols.length} cols.`);
      } else {
        parts.push('SQL updated.');
      }
    }
    if (editResult?.appliedFigure) parts.push('Chart updated.');
    if (editResult?.switchedType) parts.push(`Switched item type to ${editResult.switchedType}.`);
    if (parts.length) {
      antMessage.success(`${reason ? reason + ' — ' : ''}${parts.join(' ')}`);
    }
  };

  const handleLogInteraction = async (data) => {
    await logInteraction({
      ...data,
      session_id: sessionIdRef.current,
      context_type: 'data',
      model_key: selectedModel,
    });
  };

  const handleAddToDashboard = async (item) => {
    await addItem(item);
  };

  const handleCreateDashboard = async (name) => {
    const cleanName = (name || '').trim();
    if (!cleanName) {
      antMessage.error('Agent did not provide a dashboard name.');
      return;
    }
    try {
      const created = await createAndOpenDashboard(cleanName);
      // Navigate to the new dashboard's URL so the URL→state effect
      // doesn't immediately revert activeDashboardId back to whatever
      // was in the URL bar (which would show the dashboards list).
      if (created?.uuid) {
        navigate(`/data/chat/dashboard/${created.uuid}?mode=hybrid`);
      }
      antMessage.success(`Created dashboard "${created?.name || cleanName}"`);
    } catch (err) {
      console.error('Failed to create dashboard:', err);
      antMessage.error(`Failed to create dashboard: ${err.message}`);
    }
  };

  const handleOpenDashboardByName = async (name) => {
    const target = (name || '').trim().toLowerCase();
    if (!target) {
      antMessage.error('Agent did not specify a dashboard name.');
      return;
    }
    // Search global dashboards first (the agent's official scope),
    // then fall back to the user's personal list. Exact match wins
    // over partial.
    const pool = [
      ...((globalDashboards || []).map(d => ({ ...d, _scope: 'global' }))),
      ...((dashboards || []).map(d => ({ ...d, _scope: 'personal' }))),
    ];
    const match = pool.find(d => (d.name || '').trim().toLowerCase() === target)
      || pool.find(d => (d.name || '').toLowerCase().includes(target));
    if (!match) {
      antMessage.error(`No dashboard named "${name}" found.`);
      return;
    }
    if (!match.uuid) {
      antMessage.error(`Dashboard "${match.name}" is missing a UUID and cannot be opened.`);
      return;
    }
    // URL-based navigation. The URL→state effect in DashboardPanel
    // resolves the uuid → dashboard id, fetches the items, and opens
    // the panel. Calling switchDashboard() alone is not enough because
    // the URL effect would re-assert the previous activeDashboardId.
    navigate(`/data/chat/dashboard/${match.uuid}?mode=hybrid`);
    antMessage.success(`Opened dashboard "${match.name}"`);
  };

  const handleAddDashboardFilter = async (filterDef) => {
    try {
      await addCustomFilter(filterDef);
    } catch (err) {
      console.error('Failed to add dashboard filter:', err);
      antMessage.error(`Failed to add dashboard filter: ${err.message}`);
    }
  };

  const handleUpdateDashboardFilter = async (filterId, patch) => {
    if (!filterId) {
      antMessage.error('Agent did not specify which filter to update.');
      return;
    }
    try {
      await patchCustomFilter(filterId, patch);
    } catch (err) {
      console.error('Failed to update dashboard filter:', err);
      antMessage.error(`Failed to update dashboard filter: ${err.message}`);
    }
  };

  const handleAddDashboardTab = async (tabName) => {
    const requested = (tabName || '').trim();
    if (!requested) {
      antMessage.error('Agent did not provide a tab name.');
      return;
    }
    if (!activeDashboardId) {
      antMessage.error('No active dashboard to add a tab.');
      return;
    }

    const activeDash = (dashboards || []).find((d) => d.id === activeDashboardId);
    const tabs = Array.isArray(activeDash?.tabs) ? activeDash.tabs : [];
    const hasCollision = tabs.some((t) => String(t || '').trim().toLowerCase() === requested.toLowerCase());
    if (hasCollision) {
      antMessage.error(`Cannot create tab: "${requested}" already exists.`);
      return;
    }

    try {
      await updateDashboardMetadata(activeDashboardId, { tabs: [...tabs, requested] });
      antMessage.success(`Tab "${requested}" created`);
    } catch (err) {
      console.error('Failed to create dashboard tab:', err);
      antMessage.error(`Failed to create tab: ${err.message}`);
    }
  };

  const handleDashboardUpdate = async (componentId, updates, componentTitle) => {
    try {
      await updateItem(componentId, updates);
      antMessage.success(`Dashboard component "${componentTitle}" updated`);
    } catch (err) {
      console.error('Failed to update dashboard component:', err);
      antMessage.error(`Failed to update: ${err.message}`);
    }
  };

  const handleRenameDashboardTab = async (oldName, newName) => {
    const oldTab = (oldName || '').trim();
    const nextTab = (newName || '').trim();
    if (!oldTab || !nextTab) {
      antMessage.error('Agent did not provide both old and new tab names.');
      return;
    }
    if (!activeDashboardId) {
      antMessage.error('No active dashboard to rename tabs.');
      return;
    }

    const activeDash = (dashboards || []).find((d) => d.id === activeDashboardId);
    const tabs = Array.isArray(activeDash?.tabs) ? activeDash.tabs : [];
    const canonicalOld = tabs.find((t) => String(t || '').trim().toLowerCase() === oldTab.toLowerCase()) || oldTab;
    const hasCollision = tabs.some((t) => {
      const name = String(t || '').trim().toLowerCase();
      return name === nextTab.toLowerCase() && name !== canonicalOld.toLowerCase();
    });
    if (hasCollision) {
      antMessage.error(`Cannot rename tab: "${nextTab}" already exists.`);
      return;
    }

    try {
      await updateDashboardMetadata(activeDashboardId, { tab_renames: { [canonicalOld]: nextTab } });
      await switchDashboard(activeDashboardId, { forceReload: true });
      antMessage.success(`Tab renamed to "${nextTab}"`);
    } catch (err) {
      console.error('Failed to rename dashboard tab:', err);
      antMessage.error(`Failed to rename tab: ${err.message}`);
    }
  };

  const handleSetDefaultDashboardTab = async (tabName) => {
    if (!activeDashboardId) {
      antMessage.error('No active dashboard to set default tab.');
      return;
    }

    const requested = (tabName || '').trim();
    const activeDash = (dashboards || []).find((d) => d.id === activeDashboardId);
    const tabs = Array.isArray(activeDash?.tabs) ? activeDash.tabs : [];
    const canonical = requested
      ? (tabs.find((t) => String(t || '').trim().toLowerCase() === requested.toLowerCase()) || null)
      : '';

    if (requested && !canonical) {
      antMessage.error(`Cannot set default tab: "${requested}" does not exist.`);
      return;
    }

    try {
      const nextDefault = canonical || '';
      await updateDashboardMetadata(activeDashboardId, { default_tab: nextDefault });
      antMessage.success(nextDefault ? `"${nextDefault}" set as default tab` : 'Default tab cleared');
    } catch (err) {
      console.error('Failed to set default dashboard tab:', err);
      antMessage.error(`Failed to set default tab: ${err.message}`);
    }
  };

  // Resolve the active agent mode the same way handleSendMessage does, so
  // the system-prompt and tools previews reflect what the next chat turn
  // will actually see (default | dashboard-builder | pro-chart-editor).
  const buildActiveContexts = () => {
    if (chartEditorOpen) {
      const state = getChartEditorState() || {};
      return {
        dashboardContext: null,
        chartEditorContext: {
          sql: state.sql || '',
          columns: state.columns || [],
          sample_rows: (state.rows || []).slice(0, 20),
          figure: state.figure || null,
        },
      };
    }
    return { dashboardContext: buildDashboardContext(), chartEditorContext: null };
  };

  const handleViewSystemPrompt = async () => {
    setSystemPromptModal({ visible: true, prompt: '', loading: true });
    try {
      const { dashboardContext, chartEditorContext } = buildActiveContexts();
      const info = (dashboardContext || chartEditorContext)
        ? await getAgentInfoWithContext(dashboardContext, chartEditorContext)
        : await getAgentInfo();
      setSystemPromptModal({ visible: true, prompt: info.system_prompt || 'No system prompt available.', loading: false });
    } catch (err) {
      setSystemPromptModal({ visible: true, prompt: `Error loading system prompt: ${err.message}`, loading: false });
    }
  };

  const handleViewTools = async () => {
    setToolsModal({ visible: true, tools: [], loading: true });
    try {
      const { dashboardContext, chartEditorContext } = buildActiveContexts();
      const info = (dashboardContext || chartEditorContext)
        ? await getAgentInfoWithContext(dashboardContext, chartEditorContext)
        : await getAgentInfo();
      setToolsModal({ visible: true, tools: info.tools || [], loading: false });
    } catch (err) {
      setToolsModal({ visible: true, tools: [], loading: false, error: err.message });
    }
  };

  const settingsMenuItems = [
    {
      key: 'agent-config',
      icon: <ControlOutlined />,
      label: 'Agent Configuration…',
      onClick: () => setConfigDialogOpen(true),
    },
    {
      key: 'view-tools',
      icon: <ToolOutlined />,
      label: `View Tools${isOpen ? ' (Dashboard Active)' : ''}`,
      onClick: handleViewTools,
    },
    {
      key: 'view-system-prompt',
      icon: <FileTextOutlined />,
      label: 'View System Prompt',
      onClick: handleViewSystemPrompt,
    },
  ];

  const headerExtra = (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <Button
        type="text"
        icon={<HistoryOutlined style={{ fontSize: 18, color: historyPanelOpen ? 'var(--theme-primary)' : '#8c8c8c' }} />}
        onClick={() => setHistoryPanelOpen(prev => !prev)}
        className="agent-settings-btn"
        aria-label="Conversation History"
        title="Conversation History"
      />
      <Dropdown menu={{ items: settingsMenuItems }} trigger={['click']} placement="bottomRight">
        <Button type="text" icon={<SettingOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />} className="agent-settings-btn" />
      </Dropdown>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, position: 'relative', height: '100%', overflow: 'hidden', display: 'flex' }}>
        <div style={{ flex: 1, position: 'relative', height: '100%', overflow: 'hidden' }}>
        <ChatInterface 
        ref={chatRef}
        onSendMessage={handleSendMessage}
        onLogInteraction={handleLogInteraction}
        onAddToDashboard={handleAddToDashboard}
        onDashboardUpdate={handleDashboardUpdate}
        onAddDashboardFilter={handleAddDashboardFilter}
        onUpdateDashboardFilter={handleUpdateDashboardFilter}
        onAddDashboardTab={handleAddDashboardTab}
        onRenameDashboardTab={handleRenameDashboardTab}
        onSetDefaultDashboardTab={handleSetDefaultDashboardTab}
        onCreateDashboardByName={handleCreateDashboard}
        onOpenDashboardByName={handleOpenDashboardByName}
        onChartEditorEdit={handleChartEditorEdit}
        placeholder={chartEditorOpen ? 'Ask the agent to edit SQL or the chart\u2026' : 'Ask me anything'}
        title="Data Agent"
        agentTitle="AI ASSISTANT"
        focusedComponent={focusedComponent}
        onClearFocus={clearFocusedComponent}
        headerExtra={headerExtra}
        dashboards={dashboards}
        activeDashboardId={activeDashboardId}
        isDashboardOpen={isOpen}
        chatMode={chartEditorOpen ? 'pro-editor' : (isOpen ? 'dashboard' : 'normal')}
        filterEditorContext={filterEditorState}
        onToggleDashboard={toggleDashboard}
        onCreateDashboard={async () => {
          try {
            return await createDashboard(`Dashboard ${dashboards.length + 1}`);
          } catch (err) {
            // Surface the failure (most commonly: 20-dashboard cap from
            // the backend) instead of silently doing nothing — the menu
            // item appearing inert is more confusing than an explicit
            // "you've hit the limit" toast.
            const detail = err?.message || 'Failed to create dashboard';
            antMessage.error(detail);
            return null;
          }
        }}
      />
      {/* System Prompt Modal */}
      <Modal
        title="📋 Data Agent System Prompt"
        open={systemPromptModal.visible}
        onCancel={() => setSystemPromptModal({ visible: false, prompt: '', loading: false })}
        footer={<Button type="primary" onClick={() => setSystemPromptModal({ visible: false, prompt: '', loading: false })}>Close</Button>}
        width={800}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
      >
        {systemPromptModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : (
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.6, background: '#f5f5f5', padding: 16, borderRadius: 8, maxHeight: '65vh', overflow: 'auto' }}>
            {systemPromptModal.prompt}
          </pre>
        )}
      </Modal>

      {/* Tools Modal */}
      <Modal
        title={`🔧 Data Agent Tools (${toolsModal.tools.length})`}
        open={toolsModal.visible}
        onCancel={() => setToolsModal({ visible: false, tools: [], loading: false })}
        footer={<Button type="primary" onClick={() => setToolsModal({ visible: false, tools: [], loading: false })}>Close</Button>}
        width={700}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
      >
        {toolsModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : toolsModal.error ? (
          <div style={{ color: '#ff4d4f', padding: 16 }}>Error: {toolsModal.error}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {toolsModal.tools.map((tool, idx) => (
              <div key={idx} style={{ padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#262626', marginBottom: 4 }}>
                  🔧 {tool.name}
                </div>
                <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {(tool.description || '').substring(0, 300)}{(tool.description || '').length > 300 ? '...' : ''}
                </div>
                {tool.input_schema?.properties && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#8c8c8c' }}>
                    Parameters: {Object.keys(tool.input_schema.properties).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
      </div>
      <ConversationHistoryPanel
        isOpen={historyPanelOpen}
        onClose={() => setHistoryPanelOpen(false)}
        conversations={conversations}
        loading={conversationsLoading}
        onSelect={handleSelectConversation}
        onSearch={handleConversationSearch}
        activeSessionId={sessionIdRef.current}
      />
      <AgentConfigDialog
        visible={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        onSaved={() => { try { refreshModels && refreshModels(); } catch { /* ignore */ } }}
      />
      </div>
    </div>
  );
}

export default DataChat;
